import React, { FC, useEffect, useRef, useState } from 'react';
import Dialog, { UseDialogReturnType } from 'components/organization/dialog/dialog';

export interface SaveFilterDialogProps {
  dialog: UseDialogReturnType;
  onSave: (name: string) => void;
}

export const SaveFilterDialog: FC<SaveFilterDialogProps> = ({ dialog, onSave }) => {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const dialogEl = dialog.ref.current;
    if (!dialogEl) return;

    if (dialogEl.open) {
      inputRef.current?.focus();
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open' && dialogEl.open) {
          // Wrap in a tiny timeout to ensure dialog rendering/positioning finishes before focus
          setTimeout(() => inputRef.current?.focus(), 50);
        }
      });
    });

    observer.observe(dialogEl, { attributes: true });
    return () => observer.disconnect();
  }, [dialog.ref]);

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      setName('');
      dialog.closeDialog();
    }
  };

  return (
    <Dialog ref={dialog.ref} closeDialog={dialog.closeDialog} data-cy="save-filter-dialog">
      <div className="dialog-header">
        <h4>Save View</h4>
        <button className="icon-button" onClick={dialog.closeDialog} data-cy="save-filter-dialog-close">
          <span className="material-icons">close</span>
        </button>
      </div>
      <div className="dialog-body">
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="preset-name">
            View Name
          </label>
          <input
            id="preset-name"
            type="text"
            className="w-100 p-2"
            ref={inputRef}
            value={name}
            onChange={e => setName(e.target.value)}
            data-cy="save-filter-name-input"
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
            }}
          />
        </div>
      </div>
      <div className="dialog-footer d-flex justify-content-end" style={{ gap: '12px' }}>
        <button className="btn-outline" onClick={dialog.closeDialog} data-cy="save-filter-dialog-cancel">
          Cancel
        </button>
        <button className="btn-primary" onClick={handleSave} disabled={!name.trim()} data-cy="save-filter-dialog-save">
          Save
        </button>
      </div>
    </Dialog>
  );
};
